'use client';

import { useCallback, useEffect, useState } from 'react';

import {
  IImageListItem,
  TAssessmentImageType,
  TImageMetaPositionAlt,
  useDeviceImagesListQuery,
  useTenantConfigQuery,
} from '@northladder/apis';

import { useRouteURL } from './useRouteURL';

/**
 * This fetches the images list and filters out the target image by assessment id.
 *
 * @param imageId The assessment image id from the backend.
 * @returns - The Image data & session id
 */
export const useAssessmentImageData = (
  imageId: TAssessmentImageType | TImageMetaPositionAlt
) => {
  const [imageData, setImageData] = useState<IImageListItem>();
  const { navigateToRoute, sessionId, assessmentType, platform } =
    useRouteURL();

  const { data: configData } = useTenantConfigQuery({ identifier: platform });

  const {
    data: imagesListData,
    error: imagesListError,
    isPending: isLoadingImagesList,
    fetchDevicesImagesList,
  } = useDeviceImagesListQuery({
    assessmentType,
    baseURL: configData?.apiBaseURL || '',
    sessionId,
  });

  useEffect(() => {
    if (configData?.apiBaseURL) {
      fetchDevicesImagesList();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData?.apiBaseURL]);

  const getTargetImageFromList = useCallback(
    (newImagesData: IImageListItem[]) => {
      const targetImageFindCallback = (image: IImageListItem) => {
        if (assessmentType === 'APPLE') return image.metadata.color === imageId;

        // Use the new positioning metadata for the Grade Pro assessment type.
        return image.metadata.position === imageId;
      };

      const targetImage = newImagesData.find(targetImageFindCallback);

      setImageData(targetImage);
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imagesListData]
  );

  useEffect(() => {
    if (!imagesListData) return;

    getTargetImageFromList(imagesListData.images);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagesListData]);

  useEffect(() => {
    if (imagesListError || imageData?.status?.toLowerCase() === 'completed') {
      navigateToRoute('/');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isLoadingImagesList, imagesListError, imageData]);

  return imageData;
};
